import { useMemo, useState } from 'react';
import type { TableProps } from 'antd';
import { EyeOutlined, HistoryOutlined, RollbackOutlined } from '@ant-design/icons';
import { History } from 'lucide-react';

import { DataTable, MenuThaoTac, Tile, TileHeader } from '../../../components/shared';
import { Badge, Button, Dialog, Empty } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import type { ApiError } from '../../../lib/errors';
import { KHONG_CO, ngayGioVN, soVN } from '../../../lib/dinh-dang';
import { useDichCaiDat } from '../hooks/useDichCaiDat';
import { useKhoiPhucPhienBan, useLichSuCaiDat } from '../hooks/useBaoMat';
import type { FormCaiDat } from '../hooks/useFormCaiDat';
import type { PhienBanCaiDat } from '../types';
import { GiaTriThayDoi } from './form/GiaTriThayDoi';

/** Số tên tham số hiện trong ô "Thay đổi" trước khi gom thành `+N`. */
const TEN_TOI_DA = 2;

/**
 * Lịch sử thay đổi của chính sách tầng tenant (ADR-0059 mục 1.5, 8).
 *
 * "Khôi phục phiên bản này" là **một thay đổi mới** mang nội dung phiên bản cũ —
 * đi lại đủ kiểm biên, kiểm tự khoá và `If-Match`, nên nó có thể bị từ chối như
 * mọi lần lưu khác. Lỗi hiện trong hộp, hộp không đóng.
 */
export function LichSuThayDoi({
  tenantId,
  form,
  duocSua,
}: {
  tenantId: string;
  form: FormCaiDat;
  duocSua: boolean;
}) {
  const { t, locale } = useT();
  const d = useDichCaiDat();
  const dichLoi = useLoi();
  const q = useLichSuCaiDat(tenantId);
  const khoiPhuc = useKhoiPhucPhienBan(tenantId);
  const [xem, setXem] = useState<PhienBanCaiDat | null>(null);
  const [hoi, setHoi] = useState<PhienBanCaiDat | null>(null);

  const dong = useMemo(() => q.data?.pages.flatMap((p) => p.items) ?? [], [q.data]);
  const hienTai = form.policy?.version;

  const cot: TableProps<PhienBanCaiDat>['columns'] = [
    {
      title: t('caiDat.lichSu.cotPhienBan'),
      dataIndex: 'version',
      width: 120,
      render: (v: number) => (
        <span className="flex items-center gap-1.5 whitespace-nowrap">
          <span className="font-mono text-xs text-content-primary">#{soVN(v, locale)}</span>
          {v === hienTai && <Badge variant="success">{t('caiDat.lichSu.hienTai')}</Badge>}
        </span>
      ),
    },
    {
      title: t('caiDat.lichSu.cotThoiDiem'),
      dataIndex: 'changed_at',
      width: 150,
      render: (v: string) => (
        <span className="whitespace-nowrap text-xs text-content-muted">{ngayGioVN(v, locale)}</span>
      ),
    },
    {
      title: t('caiDat.lichSu.cotNguoiSua'),
      dataIndex: 'changed_by',
      width: 200,
      render: (v: string | null) => (
        <span className="block max-w-[12rem] truncate text-sm text-content-secondary" title={v ?? undefined}>
          {v ?? t('caiDat.lichSu.heThong')}
        </span>
      ),
    },
    {
      title: t('caiDat.lichSu.cotThayDoi'),
      width: 260,
      render: (_v, row) => {
        const ten = row.changes.map((c) => d.nhan(c.key));
        return (
          <span className="text-sm text-content-primary" title={ten.join(', ')}>
            {ten.slice(0, TEN_TOI_DA).join(', ')}
            {ten.length > TEN_TOI_DA && (
              <span className="ml-1 text-2xs text-content-muted">
                {t('caiDat.lichSu.themN', { so: soVN(ten.length - TEN_TOI_DA, locale) })}
              </span>
            )}
          </span>
        );
      },
    },
    {
      title: t('caiDat.lichSu.cotLyDo'),
      dataIndex: 'reason',
      width: 240,
      render: (v: string | null) =>
        v ? (
          <span className="line-clamp-2 text-xs text-content-secondary">{v}</span>
        ) : (
          <span className="text-content-muted">{KHONG_CO}</span>
        ),
    },
    {
      title: t('chung.thaoTac'),
      width: 72,
      align: 'center',
      fixed: 'right',
      render: (_v, row) => (
        <MenuThaoTac
          tenBanGhi={t('caiDat.lichSu.tenPhienBan', { so: soVN(row.version, locale) })}
          muc={[
            {
              khoa: 'xem',
              nhan: t('caiDat.lichSu.xemThayDoi'),
              icon: <EyeOutlined />,
              nhom: 'xem',
              onChon: () => setXem(row),
            },
            {
              khoa: 'khoi-phuc',
              nhan: t('caiDat.lichSu.khoiPhuc'),
              icon: <RollbackOutlined />,
              hien: duocSua && row.version !== hienTai,
              onChon: () => {
                khoiPhuc.reset();
                setHoi(row);
              },
            },
          ]}
        />
      ),
    },
  ];

  const loiKhoiPhuc = khoiPhuc.error as ApiError | null;

  return (
    <Tile className="overflow-hidden">
      <TileHeader
        icon={<History size={16} />}
        tone="primary"
        title={t('caiDat.lichSu.tieuDe')}
      />
      {q.error ? (
        <div role="alert" className="flex items-center justify-between gap-3 px-5 py-4">
          <span className="text-sm text-danger-700">{t('caiDat.lichSu.loi', { loi: dichLoi(q.error) })}</span>
          <Button variant="outline" size="sm" onClick={() => void q.refetch()}>
            {t('chung.thuLai')}
          </Button>
        </div>
      ) : (
        <>
          <DataTable<PhienBanCaiDat>
            caption={t('caiDat.lichSu.caption')}
            rowKey="version"
            dataSource={dong}
            columns={cot}
            loading={q.isLoading}
            size="middle"
            pagination={false}
            locale={{
              emptyText: q.isLoading ? (
                ' '
              ) : (
                <Empty icon={HistoryOutlined} title={t('caiDat.lichSu.rong')} description={t('caiDat.lichSu.rongMo')} />
              ),
            }}
          />
          {q.hasNextPage && (
            <div className="flex justify-center border-t border-default px-4 py-4">
              <Button variant="outline" size="sm" loading={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
                {t('chung.taiThem')}
              </Button>
            </div>
          )}
        </>
      )}

      <Dialog
        open={xem !== null}
        onClose={() => setXem(null)}
        title={xem ? t('caiDat.lichSu.xemTieuDe', { so: soVN(xem.version, locale) }) : ''}
        width={600}
        destroyOnHidden
        footer={
          <div className="flex justify-end">
            <Button variant="outline" size="md" onClick={() => setXem(null)}>
              {t('chung.dong')}
            </Button>
          </div>
        }
      >
        {xem && (
          <div className="space-y-3 pt-2">
            <p className="text-xs text-content-muted">
              {t('caiDat.lichSu.xemMo', {
                nguoi: xem.changed_by ?? t('caiDat.lichSu.heThong'),
                luc: ngayGioVN(xem.changed_at, locale),
              })}
            </p>
            <ul className="divide-y divide-default rounded border border-default">
              {xem.changes.map((c) => (
                <li key={c.key} className="space-y-1 px-3 py-2.5">
                  <p className="text-sm font-medium text-content-primary">{d.nhan(c.key)}</p>
                  <GiaTriThayDoi def={form.danhMuc.get(c.key)} truoc={c.before} sau={c.after} />
                </li>
              ))}
            </ul>
          </div>
        )}
      </Dialog>

      <Dialog
        open={hoi !== null}
        onClose={() => setHoi(null)}
        title={hoi ? t('caiDat.lichSu.khoiPhucTieuDe', { so: soVN(hoi.version, locale) }) : ''}
        width={480}
        destroyOnHidden
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="outline" size="md" onClick={() => setHoi(null)}>
              {t('chung.huy')}
            </Button>
            <Button
              variant="primary"
              size="md"
              loading={khoiPhuc.isPending}
              disabled={hienTai === undefined}
              onClick={() =>
                hoi &&
                hienTai !== undefined &&
                khoiPhuc.mutate(
                  { version: hoi.version, ifMatch: hienTai },
                  { onSuccess: () => setHoi(null) },
                )
              }
            >
              {hoi ? t('caiDat.lichSu.nhanKhoiPhuc', { so: soVN(hoi.version, locale) }) : ''}
            </Button>
          </div>
        }
      >
        <div className="space-y-3 pt-2">
          <p className="text-sm leading-relaxed text-content-secondary">{t('caiDat.lichSu.khoiPhucThan')}</p>
          {loiKhoiPhuc && (
            <div
              role="alert"
              className="rounded border border-danger-200 bg-danger-50 px-3 py-2.5 text-xs leading-relaxed text-danger-700"
            >
              {loiKhoiPhuc.code === 'VERSION_MISMATCH' ? t('caiDat.xungDot') : dichLoi(loiKhoiPhuc)}
            </div>
          )}
        </div>
      </Dialog>
    </Tile>
  );
}
